export interface StudentContext {
  studentId?: string;
  studentName?: string;
  studentEmail?: string;
  centerId?: string;
  directorId?: string;
  returnUrl?: string;
}

export interface ReturnContext {
  studentId?: string;
  returnUrl?: string;
  mainType?: string;
  wingType?: string;
  sessionId?: string;
}

export interface TestProgressContext {
  studentId?: string;
  currentPage: number;
  answers: Record<number, number>;
}

interface StorageEnvelope<T> {
  version: 1;
  sourceOrigin: string;
  expiresAt: number;
  payload: T;
}

const STORAGE_TTL_MS = 30 * 60 * 1000;
const DEFAULT_ALLOWED_RETURN_ORIGINS = [
  'http://localhost:5173',
  'https://app.aididactic.com',
];
const DEFAULT_ALLOWED_RETURN_PATH_PREFIXES = ['/'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isOptionalString = (value: unknown): value is string | undefined =>
  value === undefined || typeof value === 'string';

const unique = (values: string[]) => Array.from(new Set(values));

const normalizeList = (raw: string | undefined, fallback: string[]): string[] => {
  if (!raw) {
    return fallback;
  }

  const normalized = raw
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);

  return normalized.length > 0 ? normalized : fallback;
};

export const getAllowedReturnOrigins = (currentOrigin: string): string[] => {
  const envOrigins = normalizeList(
    process.env.REACT_APP_ALLOWED_RETURN_ORIGINS,
    DEFAULT_ALLOWED_RETURN_ORIGINS
  );

  return unique([currentOrigin, ...envOrigins]);
};

export const getAllowedReturnPathPrefixes = (): string[] =>
  unique(normalizeList(
    process.env.REACT_APP_ALLOWED_RETURN_PATH_PREFIXES,
    DEFAULT_ALLOWED_RETURN_PATH_PREFIXES
  ));

export const validateReturnUrl = (
  candidate: string | undefined,
  currentOrigin: string
): string | undefined => {
  if (!candidate) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(candidate, currentOrigin);

    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return undefined;
    }

    const allowedOrigins = getAllowedReturnOrigins(currentOrigin);
    if (!allowedOrigins.includes(parsedUrl.origin)) {
      return undefined;
    }

    const allowedPathPrefixes = getAllowedReturnPathPrefixes();
    const isAllowedPath = allowedPathPrefixes.some(prefix =>
      parsedUrl.pathname.startsWith(prefix)
    );

    if (!isAllowedPath) {
      return undefined;
    }

    return parsedUrl.toString();
  } catch {
    return undefined;
  }
};

export const isStudentContext = (value: unknown): value is StudentContext =>
  isRecord(value) &&
  isOptionalString(value.studentId) &&
  isOptionalString(value.studentName) &&
  isOptionalString(value.studentEmail) &&
  isOptionalString(value.centerId) &&
  isOptionalString(value.directorId) &&
  isOptionalString(value.returnUrl);

export const isReturnContext = (value: unknown): value is ReturnContext =>
  isRecord(value) &&
  isOptionalString(value.studentId) &&
  isOptionalString(value.returnUrl) &&
  isOptionalString(value.mainType) &&
  isOptionalString(value.wingType) &&
  isOptionalString(value.sessionId);

const isAnswersRecord = (value: unknown): value is Record<number, number> =>
  isRecord(value) &&
  Object.entries(value).every(
    ([key, val]) => /^\d+$/.test(key) && typeof val === 'number'
  );

export const isTestProgressContext = (value: unknown): value is TestProgressContext =>
  isRecord(value) &&
  isOptionalString(value.studentId) &&
  typeof value.currentPage === 'number' &&
  isAnswersRecord(value.answers);

export const serializeStorageRecord = <T extends object>(
  payload: T,
  currentOrigin: string,
  now = Date.now()
): string => JSON.stringify({
  version: 1,
  sourceOrigin: currentOrigin,
  expiresAt: now + STORAGE_TTL_MS,
  payload,
} satisfies StorageEnvelope<T>);

export const parseStorageRecord = <T>(
  rawValue: string | null,
  currentOrigin: string,
  validator: (value: unknown) => value is T,
  now = Date.now()
): T | undefined => {
  if (!rawValue) {
    return undefined;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as StorageEnvelope<unknown>;

    if (!isRecord(parsedValue)) {
      return undefined;
    }

    if (parsedValue.version !== 1) {
      return undefined;
    }

    if (parsedValue.sourceOrigin !== currentOrigin) {
      return undefined;
    }

    if (typeof parsedValue.expiresAt !== 'number' || parsedValue.expiresAt < now) {
      return undefined;
    }

    if (!validator(parsedValue.payload)) {
      return undefined;
    }

    return parsedValue.payload;
  } catch {
    return undefined;
  }
};

export const writeSessionStorageRecord = <T extends object>(
  key: string,
  payload: T,
  currentOrigin: string
) => {
  sessionStorage.setItem(key, serializeStorageRecord(payload, currentOrigin));
};

export const readSessionStorageRecord = <T>(
  key: string,
  currentOrigin: string,
  validator: (value: unknown) => value is T
): T | undefined => parseStorageRecord(
  sessionStorage.getItem(key),
  currentOrigin,
  validator
);
